/**
 * Interactive signal-chain graph (spec 27, 49, 50, 86, 87).
 *
 * The chain is the primary control surface: click a node to inspect it, click
 * its name to swap the hardware, click the power dot to bypass the stage. When
 * hardware changes, the node animates -- and the animation is triggered by the
 * SAME event that pushes the new profile into the worklet, so the visual
 * transition and the DSP transition are the same transition (spec 50).
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { registry } from '../../database';
import type { AnyProfile } from '../../types/hardware';
import { isHeadphone } from '../../types/hardware';
import type { CableSlotId, SlotId, StageBypass, SystemSlotKey } from '../../types/system';
import { CABLE_SLOT_LABEL, CABLE_SLOT_TYPE } from '../../types/system';
import { useApp } from '../../stores/appStore';
import { ConfidenceBadge, HardwareArt, Note, fmtPrice } from './primitives';

interface NodeSpec {
  id: string;
  label: string;
  slot?: SlotId;
  bypassKey?: keyof StageBypass;
  fixed?: string;
  /** Power cord belonging to this component, if it has an inlet. */
  powerSlot?: CableSlotId;
  /** Cable occupying the link that ARRIVES at this node. */
  linkCable?: CableSlotId;
}

export function ChainGraph() {
  const project = useApp((s) => s.project);
  const activeSystem = useApp((s) => s.activeSystem);
  const comparison = useApp((s) => s.project.comparison);
  const blind = useApp((s) => s.blind);
  const inspectorNode = useApp((s) => s.inspectorNode);
  const compiled = useApp((s) => s.compiled);
  const headroom = useApp((s) => s.headroom);
  const setInspector = useApp((s) => s.setInspector);
  const openLibrary = useApp((s) => s.openLibrary);
  const setBypass = useApp((s) => s.setBypass);
  const switchSystem = useApp((s) => s.switchSystem);
  const copySystem = useApp((s) => s.copySystem);

  const [editing, setEditing] = useState<SystemSlotKey>(activeSystem);
  useEffect(() => setEditing(activeSystem), [activeSystem]);

  const chain = editing === 'A' ? project.systemA : project.systemB;
  const out = registry.get(chain.outputId);
  const headphoneMode = !!out && isHeadphone(out);
  const sys = compiled[editing];
  const hr = headroom[editing];

  const nodes: NodeSpec[] = [
    { id: 'source', label: 'Source', fixed: 'file' },
    // The cable carrying signal INTO each box is drawn on the link arriving at
    // it, which is where it physically is.
    { id: 'dac', label: 'DAC', slot: 'dac', bypassKey: 'dacConversion', powerSlot: 'dacPower', linkCable: 'usb' },
    { id: 'amp', label: 'Amplifier', slot: 'amp', bypassKey: 'ampNonlinear', powerSlot: 'ampPower', linkCable: 'interconnect' },
    { id: 'output', label: headphoneMode ? 'Headphone' : 'Speaker', slot: 'output', bypassKey: 'speaker', linkCable: headphoneMode ? undefined : 'speakerCable' },
    {
      id: 'spatial',
      label: headphoneMode ? 'Crossfeed' : 'Room',
      bypassKey: headphoneMode ? 'spatial' : 'room',
      fixed: headphoneMode ? 'crossfeed' : 'room',
    },
    { id: 'master', label: 'Master', fixed: 'master' },
  ];

  return (
    <div className="chain-wrap">
      {comparison !== 'single' && (
        <div className="system-tabs">
          {(['A', 'B'] as SystemSlotKey[]).map((k) => {
            const c = k === 'A' ? project.systemA : project.systemB;
            const d = registry.get(c.dacId);
            const a = registry.get(c.ampId);
            const o = registry.get(c.outputId);
            const desc = blind
              ? 'Identities hidden'
              : [d?.model, a?.model, o?.model].filter(Boolean).join('  |  ') || 'Empty chain';
            return (
              <button
                key={k}
                className="system-tab"
                aria-pressed={editing === k}
                onClick={() => {
                  setEditing(k);
                  if (comparison === 'ab' || comparison === 'bank') switchSystem(k);
                }}
              >
                <b>
                  System {k}
                  {activeSystem === k && <span style={{ color: 'var(--accent)' }}> ● audible</span>}
                </b>
                <span>{desc}</span>
              </button>
            );
          })}
          <button
            className="btn sm"
            style={{ alignSelf: 'center', margin: '0 6px' }}
            title="Copy the chain you are editing onto the other system, so you can change one thing and compare"
            onClick={() => copySystem(editing, editing === 'A' ? 'B' : 'A')}
          >
            Copy {editing} → {editing === 'A' ? 'B' : 'A'}
          </button>
        </div>
      )}

      <div className="chain">
        {nodes.map((n, i) => (
          <Fragment key={n.id}>
            {i > 0 &&
              (n.linkCable ? (
                <CableLink slot={n.linkCable} system={editing} active={!!sys} />
              ) : (
                <div className={`link${sys ? ' active' : ''}`} />
              ))}
            <ChainNode
              spec={n}
              system={editing}
              selected={inspectorNode === n.id}
              blind={blind}
              onSelect={() => setInspector(inspectorNode === n.id ? null : n.id)}
              onSwap={() => n.slot && openLibrary(n.slot, editing)}
              onBypass={(v) => n.bypassKey && setBypass(editing, n.bypassKey, v)}
              bypassed={n.bypassKey ? chain.bypass[n.bypassKey] : false}
            />
          </Fragment>
        ))}
      </div>

      <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        {hr && !hr.valid && <Note tone="danger">{hr.note}</Note>}
        {hr && hr.valid && hr.headroomDb < 3 && (
          <Note tone="warn">
            Amplifier headroom is {hr.headroomDb.toFixed(1)} dB against this transducer. {hr.note} This pairing will
            audibly clip on peaks, which the model reproduces.
          </Note>
        )}
        {sys?.notes.map((note, i) => (
          <Note key={i}>{note}</Note>
        ))}
      </div>
    </div>
  );
}

/**
 * A cable drawn as the link it physically is.
 *
 * The deviation figure comes from the compiled system, so what is printed is
 * what the DSP actually did -- including "no effect" for the types the engine
 * deliberately does not model, which is never dressed up as a number.
 */
function CableLink({ slot, system, active }: { slot: CableSlotId; system: SystemSlotKey; active: boolean }) {
  const cable = useApp((s) => s.cableFor(system, slot));
  const selection = useApp((s) => (system === 'A' ? s.project.systemA : s.project.systemB).cables[slot]);
  const report = useApp((s) => s.compiled[system]?.cables?.[slot]);
  const openLibrary = useApp((s) => s.openLibrary);
  const blind = useApp((s) => s.blind);
  const modelled = CABLE_SLOT_TYPE[slot] === 'speaker' || CABLE_SLOT_TYPE[slot] === 'interconnect';

  return (
    <button
      className="cable-link"
      onClick={(e) => {
        e.stopPropagation();
        openLibrary(slot, system);
      }}
      title={report?.summary ?? `Choose a ${CABLE_SLOT_LABEL[slot].toLowerCase()}`}
    >
      <span className={`cable-line${active ? ' active' : ''}`} />
      <span className="cable-name">{blind ? 'xxxx' : cable ? cable.model : CABLE_SLOT_LABEL[slot]}</span>
      <span className="cable-meta">
        {cable ? `${selection.lengthM} m` : 'none'}
        {cable && modelled && report ? ` / ${report.maxDeviationDb < 0.005 ? '<0.01' : report.maxDeviationDb.toFixed(2)} dB` : ''}
        {cable && !modelled ? ' / no effect' : ''}
      </span>
    </button>
  );
}

function ChainNode({
  spec,
  system,
  selected,
  blind,
  bypassed,
  onSelect,
  onSwap,
  onBypass,
}: {
  spec: NodeSpec;
  system: SystemSlotKey;
  selected: boolean;
  blind: boolean;
  bypassed: boolean;
  onSelect: () => void;
  onSwap: () => void;
  onBypass: (v: boolean) => void;
}) {
  const profileFor = useApp((s) => s.profileFor);
  const metadata = useApp((s) => s.metadata);
  const chain = useApp((s) => (system === 'A' ? s.project.systemA : s.project.systemB));
  const engineSettings = useApp((s) => s.project.engine);

  const profile: AnyProfile | null = spec.slot ? profileFor(system, spec.slot) : null;
  const [swapping, setSwapping] = useState(false);
  const lastId = useRef<string | null>(profile?.id ?? null);

  useEffect(() => {
    const id = profile?.id ?? null;
    if (lastId.current !== id) {
      lastId.current = id;
      setSwapping(true);
      // Match the DSP transition length so the animation and the crossfade are
      // the same event rather than two things that happen to look similar.
      const t = setTimeout(() => setSwapping(false), Math.max(320, engineSettings.transitionMs * 4));
      return () => clearTimeout(t);
    }
  }, [profile?.id, engineSettings.transitionMs]);

  let title = '';
  let sub = '';
  if (spec.fixed === 'file') {
    title = metadata?.fileName ?? 'No file';
    sub = metadata ? `${metadata.sampleRate / 1000} kHz · ${metadata.bitDepth ?? '--'} bit · ${metadata.codec}` : 'Drop an audio file';
  } else if (spec.fixed === 'room') {
    title = chain.room.enabled ? `${chain.room.preset} room` : 'Bypassed';
    sub = chain.room.enabled ? `${chain.room.widthM} × ${chain.room.lengthM} m · RT60 ${chain.room.rt60.toFixed(2)} s` : 'Anechoic';
  } else if (spec.fixed === 'crossfeed') {
    title = chain.headphone.crossfeed === 'off' ? 'Off' : chain.headphone.crossfeed;
    sub = 'Interaural path';
  } else if (spec.fixed === 'master') {
    title = `${engineSettings.masterGainDb.toFixed(1)} dB`;
    sub = engineSettings.safetyLimiter ? 'Safety limiter on' : 'Limiter off';
  } else if (profile) {
    title = blind ? '••••••' : profile.model;
    sub = blind ? '' : profile.manufacturer;
  } else {
    title = 'Empty';
    sub = 'Click to choose';
  }

  return (
    <div
      className={`node${selected ? ' selected' : ''}${bypassed ? ' bypassed' : ''}${profile || spec.fixed ? '' : ' empty'}${swapping ? ' swapping' : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-label={`${spec.label}: ${title}`}
    >
      <div className="slot">
        {spec.label}
        {spec.bypassKey && (
          <button
            className="fav"
            style={{ float: 'right', color: bypassed ? 'var(--text-faint)' : 'var(--accent)' }}
            title={bypassed ? 'Stage bypassed — click to enable' : 'Stage active — click to bypass'}
            onClick={(e) => {
              e.stopPropagation();
              onBypass(!bypassed);
            }}
          >
            ⏻
          </button>
        )}
      </div>

      {profile && !blind && (
        <div className="node-art">
          <HardwareArt profile={profile} height={22} />
        </div>
      )}

      {sub && <div className="mfr">{sub}</div>}
      <div
        className="model"
        onClick={(e) => {
          if (spec.slot) {
            e.stopPropagation();
            onSwap();
          }
        }}
        style={spec.slot ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--line-strong)', textUnderlineOffset: 3 } : undefined}
        title={spec.slot ? 'Click to change hardware' : undefined}
      >
        {title}
      </div>

      {spec.powerSlot && <PowerCordRow slot={spec.powerSlot} system={system} />}

      <div className="meta">
        {profile && !blind && <ConfidenceBadge value={profile.confidence} label={false} />}
        {profile && !blind && <span className="tag">{fmtPrice(profile.price)}</span>}
        {bypassed && <span className="tag" style={{ color: 'var(--warn)' }}>BYPASS</span>}
      </div>
    </div>
  );
}


/** Power cord for one component. Always shown; never claims a signal effect. */
function PowerCordRow({ slot, system }: { slot: CableSlotId; system: SystemSlotKey }) {
  const cable = useApp((s) => s.cableFor(system, slot));
  const selection = useApp((s) => (system === 'A' ? s.project.systemA : s.project.systemB).cables[slot]);
  const openLibrary = useApp((s) => s.openLibrary);
  const blind = useApp((s) => s.blind);
  return (
    <button
      className="power-row"
      onClick={(e) => {
        e.stopPropagation();
        openLibrary(slot, system);
      }}
      title="Power cord. The engine applies no signal processing for power cables."
    >
      <span className="faint">PWR</span>
      <span className="power-name">{blind ? 'xxxx' : cable ? cable.model : 'No power cord'}</span>
      {cable && <span className="faint tiny">{selection.lengthM} m</span>}
    </button>
  );
}
